import { createDefaultComposition } from '../../composition/model';
import { isOptionalLayerDisplayFilterValid } from '../../displayFilters/displayFilter';
import { isTableSource, isVectorSource } from '../../data/model';
import { prepareLayerReplacement } from '../../data/replacement';
import { isCapabilityAvailable } from '../../licensing/entitlements';
import { extentForLayers } from '../../map/extentForSources';
import { viewForExtent } from '../../map/viewForExtent';
import { isLayerLabelConfigurationValid } from '../../labels/validation';
import {
  isLayerPopupConfigurationValid,
  popupConfigurationHasContent,
} from '../../popups/validation';
import { createImportedVectorProjectItems } from '../../project/createImportedVectorLayer';
import { createImportedTableSource } from '../../project/createImportedTable';
import {
  createEmptyProject,
  DEFAULT_PROJECT_TITLE,
  type LayerDefinition,
  type ProjectModelDraft,
} from '../../project/model';
import { reorderLayers } from '../../project/reorderLayers';
import { isVectorLayerStyleValid } from '../../styles/validation';
import { calculateJoinDiagnostics, canApplyJoin } from '../../joins/diagnostics';
import { effectiveVectorSourceForLayer } from '../../joins/effectiveLayer';
import { joinDependencies } from '../../joins/dependencies';
import { createJoinConfig, isExactJoinRelationship, updateJoinConfig } from '../../joins/model';
import {
  planJoinRemoval,
  summarizeJoinDependencies,
  type JoinDependencySummaryArea,
} from '../../joins/removal';
import { removeSelectedFeaturesForLayer } from '../../selection/selection';
import { canTransitionMode, modeForOpenedInput } from '../modes/applicationModes';
import { ACTION_OK, actionError, type AppStoreActionContext } from './actionSupport';
import { DEFAULT_SHELL_UI_STATE, type AppState } from './appState';
import { layerStateContext } from './layerState';

function joinDependencyAreaList(areas: readonly JoinDependencySummaryArea[]): string {
  const labels = areas.map((area) => area.toLowerCase());
  if (labels.length < 2) return labels[0] ?? 'layer settings';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels.at(-1)}`;
}

function importMetadata(project: ProjectModelDraft, suggestedTitle: string) {
  return project.sources.length === 0 && project.metadata.title === DEFAULT_PROJECT_TITLE
    ? { ...project.metadata, title: suggestedTitle }
    : project.metadata;
}

function joinDependencyError(
  action: 'edited' | 'removed',
  areas: readonly JoinDependencySummaryArea[],
  fieldLabels: readonly string[],
): ReturnType<typeof actionError> {
  const fieldSummary =
    fieldLabels.length === 0
      ? ''
      : ` ${fieldLabels.length === 1 ? 'Field' : 'Fields'} in use: ${fieldLabels.join(', ')}.`;
  return actionError(
    `This join cannot be ${action} because joined fields are currently used by the layer ${joinDependencyAreaList(areas)}.${fieldSummary}`,
  );
}

export type ProjectActions = Pick<
  AppState,
  | 'transitionTo'
  | 'openBlankMap'
  | 'showSearchTarget'
  | 'clearSearchNavigation'
  | 'openInput'
  | 'addImportedVector'
  | 'addImportedVectors'
  | 'addImportedTable'
  | 'addImportedGeoPackage'
  | 'closeAttributeTable'
  | 'closeInspector'
  | 'closeFeatureInspection'
  | 'fitToData'
  | 'newMap'
  | 'openAttributeTable'
  | 'replaceProject'
  | 'replaceLayerData'
  | 'removeLayer'
  | 'renameLayer'
  | 'removeTableSource'
  | 'reorderLayer'
  | 'selectLayer'
  | 'viewLayerData'
  | 'viewTableData'
  | 'addLayerJoin'
  | 'updateLayerJoin'
  | 'inspectLayerJoinRemoval'
  | 'removeLayerJoin'
>;

export function createProjectActions({ get, set }: AppStoreActionContext): ProjectActions {
  return {
    openBlankMap: () =>
      set({ mode: 'editor', project: createEmptyProject(), shell: DEFAULT_SHELL_UI_STATE }),
    showSearchTarget: (target) =>
      set((state) => ({ shell: { ...state.shell, searchTarget: target } })),
    clearSearchNavigation: () =>
      set((state) =>
        state.shell.searchTarget ? { shell: { ...state.shell, searchTarget: null } } : state,
      ),
    transitionTo: (nextMode) =>
      set((state) => {
        if (!canTransitionMode(state.mode, nextMode)) return state;
        if (state.mode === nextMode) return state;
        if (
          nextMode === 'composition' &&
          !isCapabilityAvailable(state.entitlement, 'composition.workspace')
        ) {
          return state;
        }
        const enteringComposition = nextMode === 'composition';
        return {
          mode: nextMode,
          shell: {
            ...state.shell,
            attributeTableOpen: false,
            inspectedFeature: null,
            selectionToolActive: enteringComposition ? false : state.shell.selectionToolActive,
            selectionMethod: enteringComposition ? null : state.shell.selectionMethod,
            selectedFeatures: enteringComposition ? [] : state.shell.selectedFeatures,
          },
        };
      }),
    openInput: (input) =>
      set((state) => ({
        mode: modeForOpenedInput(input),
        shell: {
          ...state.shell,
          attributeTableOpen: false,
          inspectedFeature: null,
          selectionToolActive: false,
          selectionMethod: null,
          selectedFeatures: [],
        },
      })),
    addImportedVector: (imported) =>
      set((state) => {
        const { source, layer } = createImportedVectorProjectItems(
          imported,
          undefined,
          state.project.map.basemap.presetId === 'dropmap-dark' ? 'dark' : 'light',
        );
        const view = source.extent ? viewForExtent(source.extent) : state.project.map.view;

        return {
          mode: 'editor',
          shell: {
            ...state.shell,
            selectedTableSourceId: null,
            selectedLayerId: layer.id,
            inspectorOpen: true,
          },
          project: {
            ...state.project,
            metadata: importMetadata(state.project, layer.name),
            map: { ...state.project.map, view },
            composition:
              state.mode === 'empty' && state.project.sources.length === 0
                ? createDefaultComposition(view, layer.name)
                : state.project.composition,
            sources: [...state.project.sources, source],
            layers: [...state.project.layers, layer],
          },
        };
      }),
    addImportedVectors: (imported) =>
      set((state) => {
        if (!imported.length) return state;
        const usedNames = new Set(
          state.project.layers.map((layer) => layer.name.toLocaleLowerCase()),
        );
        const items = imported.map((value) => {
          const baseName = value.suggestedLayerName;
          let name = baseName;
          for (let suffix = 2; usedNames.has(name.toLocaleLowerCase()); suffix += 1) {
            name = `${baseName} (${suffix})`;
          }
          usedNames.add(name.toLocaleLowerCase());
          const item = createImportedVectorProjectItems(
            value,
            undefined,
            state.project.map.basemap.presetId === 'dropmap-dark' ? 'dark' : 'light',
          );
          return { source: { ...item.source, name }, layer: { ...item.layer, name } };
        });
        const first = items[0]!;
        const view = first.source.extent
          ? viewForExtent(first.source.extent)
          : state.project.map.view;
        return {
          mode: 'editor',
          shell: {
            ...state.shell,
            selectedTableSourceId: null,
            selectedLayerId: first.layer.id,
            inspectorOpen: true,
          },
          project: {
            ...state.project,
            metadata: importMetadata(state.project, first.layer.name),
            map: { ...state.project.map, view },
            composition:
              state.mode === 'empty' && state.project.sources.length === 0
                ? createDefaultComposition(view, first.layer.name)
                : state.project.composition,
            sources: [...state.project.sources, ...items.map((item) => item.source)],
            layers: [...state.project.layers, ...items.map((item) => item.layer)],
          },
        };
      }),
    addImportedTable: (imported) =>
      set((state) => {
        const source = createImportedTableSource(imported);
        return {
          mode: 'editor',
          shell: {
            ...state.shell,
            selectedLayerId: null,
            selectedTableSourceId: source.id,
            inspectorOpen: false,
            attributeTableOpen: true,
          },
          project: {
            ...state.project,
            metadata: importMetadata(state.project, source.name),
            sources: [...state.project.sources, source],
          },
        };
      }),
    addImportedGeoPackage: (imported) =>
      set((state) => {
        if (!imported.items.length) return state;
        const usedLayerNames = new Set(
          state.project.layers.map((layer) => layer.name.toLocaleLowerCase()),
        );
        const usedTableNames = new Set(
          state.project.sources
            .filter(isTableSource)
            .map((source) => source.name.toLocaleLowerCase()),
        );
        const uniqueName = (base: string, used: Set<string>) => {
          let name = base;
          for (let suffix = 2; used.has(name.toLocaleLowerCase()); suffix += 1)
            name = `${base} (${suffix})`;
          used.add(name.toLocaleLowerCase());
          return name;
        };
        const sources = [...state.project.sources];
        const layers = [...state.project.layers];
        let firstLayer: LayerDefinition | null = null;
        let firstExtent = null as ReturnType<typeof viewForExtent> | null;
        let firstTableId: string | null = null;
        for (const item of imported.items) {
          if ('data' in item) {
            const pair = createImportedVectorProjectItems(
              item,
              undefined,
              state.project.map.basemap.presetId === 'dropmap-dark' ? 'dark' : 'light',
            );
            const name = uniqueName(pair.layer.name, usedLayerNames);
            const layer = { ...pair.layer, name };
            sources.push({ ...pair.source, name });
            layers.push(layer);
            if (!firstLayer) {
              firstLayer = layer;
              firstExtent = pair.source.extent ? viewForExtent(pair.source.extent) : null;
            }
          } else {
            const table = createImportedTableSource(item);
            sources.push({ ...table, name: uniqueName(table.name, usedTableNames) });
            firstTableId ??= table.id;
          }
        }
        const view = firstExtent ?? state.project.map.view;
        const title =
          firstLayer?.name ??
          sources.find((source) => source.id === firstTableId)?.name ??
          state.project.metadata.title;
        return {
          mode: 'editor',
          shell: {
            ...state.shell,
            selectedLayerId: firstLayer?.id ?? null,
            selectedTableSourceId: firstLayer ? null : firstTableId,
            inspectorOpen: Boolean(firstLayer),
            attributeTableOpen: !firstLayer && Boolean(firstTableId),
          },
          project: {
            ...state.project,
            metadata: importMetadata(state.project, title),
            map: { ...state.project.map, view },
            composition:
              firstLayer && state.mode === 'empty' && state.project.sources.length === 0
                ? createDefaultComposition(view, title)
                : state.project.composition,
            sources,
            layers,
          },
        };
      }),
    closeAttributeTable: () =>
      set((state) =>
        state.shell.attributeTableOpen
          ? { shell: { ...state.shell, attributeTableOpen: false } }
          : state,
      ),
    closeInspector: () =>
      set((state) => ({
        shell: { ...state.shell, inspectorOpen: false, selectedAnnotationId: null },
      })),
    closeFeatureInspection: () =>
      set((state) =>
        state.shell.inspectedFeature
          ? { shell: { ...state.shell, inspectedFeature: null } }
          : state,
      ),
    fitToData: (viewport) =>
      set((state) => {
        const extent = extentForLayers(state.project.sources, state.project.layers);
        if (!extent) return state;
        return {
          project: {
            ...state.project,
            map: { ...state.project.map, view: viewForExtent(extent, viewport) },
          },
          shell: { ...state.shell, inspectedFeature: null },
        };
      }),
    newMap: () =>
      set({
        mode: 'empty',
        project: createEmptyProject(),
        shell: DEFAULT_SHELL_UI_STATE,
      }),
    openAttributeTable: () =>
      set((state) => {
        if (
          (state.mode !== 'editor' && state.mode !== 'viewer') ||
          state.shell.attributeTableOpen
        ) {
          return state;
        }
        const layer = state.project.layers.find(
          (candidate) => candidate.id === state.shell.selectedLayerId,
        );
        const source = layer
          ? state.project.sources.find((candidate) => candidate.id === layer.sourceId)
          : undefined;
        if (!layer || !source || !isVectorSource(source)) return state;
        const effective = effectiveVectorSourceForLayer(state.project.sources, layer);
        if (!effective || effective.fields.length === 0) return state;
        return {
          shell: { ...state.shell, selectedTableSourceId: null, attributeTableOpen: true },
        };
      }),
    replaceProject: (project) =>
      set({
        mode: 'viewer',
        project,
        shell: DEFAULT_SHELL_UI_STATE,
      }),
    replaceLayerData: (layerId, imported, resolutions) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'data.refresh')) {
        return actionError('Replacing layer data requires Placeio Maps Pro.');
      }
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const sourceUseCount = state.project.layers.filter(
        (layer) => layer.sourceId === context.canonicalSource.id,
      ).length;
      if (sourceUseCount !== 1) {
        return actionError('This source is shared by multiple layers and cannot be replaced here.');
      }
      const replacement = prepareLayerReplacement(
        state.project.sources,
        context.layer,
        context.canonicalSource,
        imported,
        resolutions,
      );
      if (!replacement.ok) return actionError(replacement.message);
      set({
        project: {
          ...state.project,
          sources: state.project.sources.map((source) =>
            source.id === replacement.source.id ? replacement.source : source,
          ),
          layers: state.project.layers.map((layer) =>
            layer.id === layerId ? replacement.layer : layer,
          ),
        },
        shell: {
          ...state.shell,
          selectedFeatures: removeSelectedFeaturesForLayer(state.shell.selectedFeatures, layerId),
          inspectedFeature:
            state.shell.inspectedFeature?.layerId === layerId ? null : state.shell.inspectedFeature,
        },
      });
      return ACTION_OK;
    },
    removeLayer: (layerId) =>
      set((state) => {
        const layers = state.project.layers.filter((layer) => layer.id !== layerId);
        if (layers.length === state.project.layers.length) return state;
        const tables = state.project.sources.filter(isTableSource);
        if (
          layers.length === 0 &&
          tables.length === 0 &&
          state.project.annotationLayers.length === 0 &&
          state.project.symbolAssets.length === 0
        ) {
          return {
            mode: 'empty',
            project: createEmptyProject(),
            shell: DEFAULT_SHELL_UI_STATE,
          };
        }

        const referencedSourceIds = new Set(layers.map((layer) => layer.sourceId));
        const selectedLayerId =
          state.shell.selectedLayerId === layerId
            ? (layers[0]?.id ?? null)
            : state.shell.selectedLayerId;
        return {
          project: {
            ...state.project,
            sources: state.project.sources.filter(
              (source) => !isVectorSource(source) || referencedSourceIds.has(source.id),
            ),
            layers,
            composition: {
              ...state.project.composition,
              legend: {
                ...state.project.composition.legend,
                excludedLayerIds: state.project.composition.legend.excludedLayerIds.filter(
                  (id) => id !== layerId,
                ),
                hiddenGroupHeadingIds:
                  state.project.composition.legend.hiddenGroupHeadingIds.filter(
                    (id) => id !== layerId,
                  ),
                excludedEntryKeysByLayer: Object.fromEntries(
                  Object.entries(state.project.composition.legend.excludedEntryKeysByLayer).filter(
                    ([id]) => id !== layerId,
                  ),
                ),
              },
            },
          },
          shell: {
            ...state.shell,
            selectedLayerId,
            selectedTableSourceId:
              selectedLayerId === null
                ? (state.shell.selectedTableSourceId ?? tables[0]?.id ?? null)
                : null,
            expandedLayerIds: state.shell.expandedLayerIds.filter((id) => id !== layerId),
            inspectorOpen: selectedLayerId !== null,
            selectedFeatures: removeSelectedFeaturesForLayer(state.shell.selectedFeatures, layerId),
            inspectedFeature:
              state.shell.inspectedFeature?.layerId === layerId
                ? null
                : state.shell.inspectedFeature,
          },
        };
      }),
    renameLayer: (layerId, name) => {
      const normalisedName = name.trim();
      if (normalisedName.length === 0 || normalisedName.length > 120) {
        return actionError('Layer names must contain 1 to 120 characters.');
      }
      const state = get();
      const index = state.project.layers.findIndex((layer) => layer.id === layerId);
      if (index < 0) return actionError('The selected layer is unavailable.');
      const layers = [...state.project.layers];
      layers[index] = { ...layers[index]!, name: normalisedName };
      set({ project: { ...state.project, layers } });
      return ACTION_OK;
    },
    removeTableSource: (sourceId) => {
      const state = get();
      const source = state.project.sources.find((candidate) => candidate.id === sourceId);
      if (!source || !isTableSource(source)) return actionError('The table is unavailable.');
      const usedBy = state.project.layers.filter((layer) =>
        (layer.joins ?? []).some((join) => join.tableSourceId === sourceId),
      );
      const dependentJoinCount = state.project.layers.reduce(
        (count, layer) =>
          count + (layer.joins ?? []).filter((join) => join.tableSourceId === sourceId).length,
        0,
      );
      if (dependentJoinCount > 0) {
        return actionError(
          `This table is used by ${dependentJoinCount.toLocaleString()} ${dependentJoinCount === 1 ? 'join' : 'joins'}. Remove ${dependentJoinCount === 1 ? 'that join' : 'those joins'} before deleting it. Affected ${usedBy.length === 1 ? 'layer' : 'layers'}: ${usedBy.map((layer) => layer.name).join(', ')}.`,
        );
      }
      const sources = state.project.sources.filter((candidate) => candidate.id !== sourceId);
      set({
        mode:
          sources.length === 0 && state.project.symbolAssets.length === 0 ? 'empty' : state.mode,
        project:
          sources.length === 0 && state.project.symbolAssets.length === 0
            ? createEmptyProject()
            : { ...state.project, sources },
        shell:
          sources.length === 0 && state.project.symbolAssets.length === 0
            ? DEFAULT_SHELL_UI_STATE
            : {
                ...state.shell,
                selectedTableSourceId:
                  state.shell.selectedTableSourceId === sourceId
                    ? null
                    : state.shell.selectedTableSourceId,
                attributeTableOpen:
                  state.shell.selectedTableSourceId === sourceId
                    ? false
                    : state.shell.attributeTableOpen,
              },
      });
      return ACTION_OK;
    },
    reorderLayer: (layerId, targetIndex) =>
      set((state) => {
        const layers = reorderLayers(state.project.layers, layerId, targetIndex);
        if (layers === state.project.layers) return state;
        return { project: { ...state.project, layers } };
      }),
    selectLayer: (layerId) =>
      set((state) => {
        if (!state.project.layers.some((layer) => layer.id === layerId)) return state;
        return {
          shell: {
            ...state.shell,
            selectedLayerId: layerId,
            selectedTableSourceId: null,
            selectedAnnotationId: null,
            inspectorOpen: state.mode === 'editor' ? true : state.shell.inspectorOpen,
          },
        };
      }),
    viewLayerData: (layerId) =>
      set((state) => {
        if (state.mode !== 'editor' && state.mode !== 'viewer') return state;
        const layer = state.project.layers.find((candidate) => candidate.id === layerId);
        const source = layer
          ? state.project.sources.find((candidate) => candidate.id === layer.sourceId)
          : undefined;
        const effective = layer
          ? effectiveVectorSourceForLayer(state.project.sources, layer)
          : null;
        if (
          !layer ||
          !source ||
          !isVectorSource(source) ||
          !effective ||
          effective.fields.length === 0
        ) {
          return state;
        }
        return {
          shell: {
            ...state.shell,
            selectedLayerId: layer.id,
            selectedTableSourceId: null,
            inspectorOpen: state.mode === 'editor' ? true : state.shell.inspectorOpen,
            attributeTableOpen: true,
          },
        };
      }),
    viewTableData: (sourceId) =>
      set((state) => {
        if (state.mode !== 'editor' && state.mode !== 'viewer') return state;
        const source = state.project.sources.find((candidate) => candidate.id === sourceId);
        if (!source || !isTableSource(source) || source.fields.length === 0) return state;
        return {
          shell: {
            ...state.shell,
            selectedLayerId: null,
            selectedTableSourceId: source.id,
            inspectorOpen: false,
            attributeTableOpen: true,
          },
        };
      }),
    addLayerJoin: (layerId, draft) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      const table = state.project.sources.find(
        (source) => source.id === draft.tableSourceId && isTableSource(source),
      );
      if (!context || !table || !isTableSource(table)) {
        return actionError('The layer or table is unavailable.');
      }
      if ((context.layer.joins ?? []).some((join) => isExactJoinRelationship(join, draft))) {
        return actionError('This join already exists.');
      }
      if (
        draft.fields.length === 0 ||
        new Set(draft.fields).size !== draft.fields.length ||
        draft.fields.some((field) => !table.fields.some((candidate) => candidate.name === field))
      ) {
        return actionError('Choose one or more available table fields to add.');
      }
      const diagnostics = calculateJoinDiagnostics(
        context.canonicalSource,
        table,
        draft.leftField,
        draft.rightField,
      );
      if (!canApplyJoin(diagnostics, draft.fields.length)) {
        const message =
          diagnostics.incompatibility ??
          (diagnostics.duplicateRightKeyCount > 0
            ? `Cannot join: ${diagnostics.duplicateRightKeyCount.toLocaleString()} duplicate table key ${diagnostics.duplicateRightKeyCount === 1 ? 'value' : 'values'}.`
            : diagnostics.matchedFeatureCount === 0
              ? 'Cannot join because no features match.'
              : 'Choose one or more fields to add.');
        return actionError(message);
      }
      const join = createJoinConfig(draft, table.name);
      set({
        project: {
          ...state.project,
          layers: state.project.layers.map((layer) =>
            layer.id === layerId ? { ...layer, joins: [...(layer.joins ?? []), join] } : layer,
          ),
        },
      });
      return ACTION_OK;
    },
    updateLayerJoin: (layerId, joinId, draft) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      const current = context?.layer.joins.find((join) => join.id === joinId);
      const table = state.project.sources.find(
        (source) => source.id === draft.tableSourceId && isTableSource(source),
      );
      if (!context || !current || !table || !isTableSource(table)) {
        return actionError('The join, layer, or table is unavailable.');
      }
      if (
        context.layer.joins.some(
          (join) => join.id !== joinId && isExactJoinRelationship(join, draft),
        )
      ) {
        return actionError('This join already exists.');
      }
      if (
        draft.fields.length === 0 ||
        new Set(draft.fields).size !== draft.fields.length ||
        draft.fields.some((field) => !table.fields.some((candidate) => candidate.name === field))
      ) {
        return actionError('Choose one or more available table fields to add.');
      }
      const diagnostics = calculateJoinDiagnostics(
        context.canonicalSource,
        table,
        draft.leftField,
        draft.rightField,
      );
      if (!canApplyJoin(diagnostics, draft.fields.length)) {
        const message =
          diagnostics.incompatibility ??
          (diagnostics.duplicateRightKeyCount > 0
            ? `Cannot join: ${diagnostics.duplicateRightKeyCount.toLocaleString()} duplicate table key ${diagnostics.duplicateRightKeyCount === 1 ? 'value' : 'values'}.`
            : diagnostics.matchedFeatureCount === 0
              ? 'Cannot join because no features match.'
              : 'Choose one or more fields to add.');
        return actionError(message);
      }

      const nextJoin = updateJoinConfig(current, draft, table.name);
      const previousTable = state.project.sources.find(
        (source) => source.id === current.tableSourceId && isTableSource(source),
      );
      const nextFieldsById = new Map(nextJoin.fields.map((field) => [field.outputFieldId, field]));
      const invalidatedFieldIds = current.fields.flatMap((field) => {
        const next = nextFieldsById.get(field.outputFieldId);
        if (!next) return [field.outputFieldId];
        const previousType = previousTable?.fields.find(
          (candidate) => candidate.name === field.sourceField,
        )?.type;
        const nextType = table.fields.find(
          (candidate) => candidate.name === next.sourceField,
        )?.type;
        return previousType === nextType ? [] : [field.outputFieldId];
      });
      const dependencies = joinDependencies(context.layer, invalidatedFieldIds);
      if (dependencies.length > 0) {
        const invalidated = new Set(invalidatedFieldIds);
        return joinDependencyError(
          'edited',
          summarizeJoinDependencies(dependencies),
          current.fields
            .filter((field) => invalidated.has(field.outputFieldId))
            .map((field) => field.displayLabel),
        );
      }

      const nextLayer: LayerDefinition = {
        ...context.layer,
        joins: context.layer.joins.map((join) => (join.id === joinId ? nextJoin : join)),
      };
      const effectiveSource = effectiveVectorSourceForLayer(state.project.sources, nextLayer);
      if (
        !effectiveSource ||
        !isVectorLayerStyleValid(nextLayer.style, effectiveSource.fields) ||
        !isLayerLabelConfigurationValid(nextLayer.labels, effectiveSource.fields) ||
        !isLayerPopupConfigurationValid(nextLayer.popup, effectiveSource.fields) ||
        !isOptionalLayerDisplayFilterValid(nextLayer.displayFilter, effectiveSource.fields)
      ) {
        return actionError(
          'This join cannot be edited because the changes would leave invalid layer settings.',
        );
      }
      set({
        project: {
          ...state.project,
          layers: state.project.layers.map((layer) => (layer.id === layerId ? nextLayer : layer)),
        },
      });
      return ACTION_OK;
    },
    inspectLayerJoinRemoval: (layerId, joinId) => {
      const state = get();
      const layer = state.project.layers.find((candidate) => candidate.id === layerId);
      if (!layer) return actionError('The join is unavailable.');
      const plan = planJoinRemoval(layer, joinId);
      if (!plan) return actionError('The join is unavailable.');
      if (plan.dependencies.length > 0) {
        return joinDependencyError(
          'removed',
          plan.summaryAreas,
          plan.join.fields
            .filter((field) => joinDependencies(layer, [field.outputFieldId]).length > 0)
            .map((field) => field.displayLabel),
        );
      }
      return {
        ok: true,
        impact: { joinName: plan.join.name },
      };
    },
    removeLayerJoin: (layerId, joinId) => {
      const state = get();
      const layer = state.project.layers.find((candidate) => candidate.id === layerId);
      const plan = layer ? planJoinRemoval(layer, joinId) : null;
      if (!plan) return actionError('The join is unavailable.');
      const inspection = get().inspectLayerJoinRemoval(layerId, joinId);
      if (!inspection.ok) return inspection;
      const effectiveSource = effectiveVectorSourceForLayer(state.project.sources, plan.layer);
      if (
        !effectiveSource ||
        !isVectorLayerStyleValid(plan.layer.style, effectiveSource.fields) ||
        !isLayerLabelConfigurationValid(plan.layer.labels, effectiveSource.fields) ||
        !isLayerPopupConfigurationValid(plan.layer.popup, effectiveSource.fields) ||
        !isOptionalLayerDisplayFilterValid(plan.layer.displayFilter, effectiveSource.fields)
      ) {
        return actionError('This join cannot be removed without leaving invalid settings.');
      }
      set({
        project: {
          ...state.project,
          layers: state.project.layers.map((candidate) =>
            candidate.id === layerId ? plan.layer : candidate,
          ),
        },
        shell: {
          ...state.shell,
          inspectedFeature:
            state.shell.inspectedFeature?.layerId === layerId &&
            !popupConfigurationHasContent(plan.layer.popup)
              ? null
              : state.shell.inspectedFeature,
        },
      });
      return ACTION_OK;
    },
  };
}
